import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface HeroSlide {
  id: string;
  title: string;
  meta: string;
  desc: string;
  image: string;
}

const SLIDES: HeroSlide[] = [
  {
    id: "long-night-coming",
    title: "长夜将尽",
    meta: "2024 · 中国大陆 · 犯罪悬疑",
    desc: "一桩尘封十七年的旧案重新浮出水面，前刑警与年轻记者在雨夜的城市里追查真相。",
    image:
      "https://readdy.ai/api/search-image?query=Atmospheric%20cinematic%20wide%20shot%20of%20a%20rain%20soaked%20neon%20city%20alley%20at%20night%20with%20deep%20navy%20shadows%20and%20a%20single%20warm%20orange%20streetlamp%2C%20thick%20fog%2C%20film%20noir%20mood%2C%20abstract%20stylized%20illustration%20with%20dark%20tones%20and%20empty%20space%20for%20text&width=1920&height=1080&seq=marie-hero-01&orientation=landscape",
  },
  {
    id: "star-sea-dream",
    title: "星海遗梦",
    meta: "2025 · 美国 · 科幻冒险",
    desc: "一支深空考察队抵达人类最后的移民候选星球，却发现那里保持着一段不属于他们的记忆。",
    image:
      "https://readdy.ai/api/search-image?query=Epic%20cinematic%20wide%20space%20scene%20of%20a%20lone%20spacecraft%20before%20a%20giant%20glowing%20planet%20ring%2C%20deep%20navy%20void%20with%20vivid%20orange%20nebula%20glow%2C%20dramatic%20dark%20composition%20with%20negative%20space%2C%20stylized%20sci%20fi%20illustration&width=1920&height=1080&seq=marie-hero-02&orientation=landscape",
  },
  {
    id: "mist-harbor",
    title: "雾港之谜",
    meta: "2025 · 中国大陆 · 犯罪悬疑剧",
    desc: "南方小港连续出现无名浮尸，退休海警与年轻法医联手追查，牵出一张横跨二十年的网。",
    image:
      "https://readdy.ai/api/search-image?query=Moody%20cinematic%20wide%20shot%20of%20a%20fishing%20harbor%20shrouded%20in%20thick%20morning%20mist%20with%20a%20solitary%20lit%20lamp%20post%2C%20cold%20navy%20blue%20and%20faded%20orange%20tones%2C%20quiet%20ominous%20atmosphere%20for%20text%20overlay%2C%20stylized%20illustration&width=1920&height=1080&seq=marie-hero-03&orientation=landscape",
  },
  {
    id: "mountain-sea-scroll",
    title: "山海绘卷",
    meta: "2024 · 中国大陆 · 奇幻动画",
    desc: "少女画师意外进入家族流传的神秘画卷，画卷里的神兽正因笔墨褪色而逐渐消失。",
    image:
      "https://readdy.ai/api/search-image?query=Chinese%20mythological%20ink%20painting%20wide%20scene%20of%20a%20giant%20elegant%20beast%20coiling%20around%20misty%20mountain%20peaks%2C%20deep%20navy%20and%20vivid%20orange%20accents%2C%20ethereal%20epic%20atmosphere%20with%20open%20space%20for%20text%2C%20stylized%20illustration&width=1920&height=1080&seq=marie-hero-04&orientation=landscape",
  },
  {
    id: "deep-blue-echo",
    title: "深蓝回响",
    meta: "2025 · 美国 · 科幻惊悚剧",
    desc: "深海研究站收到一段来自海沟深处的重复信号，内容是他们自己三小时后的对话。",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20deep%20sea%20research%20station%20glowing%20in%20dark%20ocean%20water%2C%20bioluminescent%20orange%20particles%20drifting%20around%2C%20overwhelming%20deep%20navy%20abyss%20with%20open%20negative%20space%2C%20eerie%20stylized%20illustration&width=1920&height=1080&seq=marie-hero-05&orientation=landscape",
  },
];

const AUTOPLAY_MS = 6000;

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const go = (next: number) => {
    setIndex(((next % SLIDES.length) + SLIDES.length) % SLIDES.length);
  };

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [index]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
    touchStartX.current = null;
  };

  const slide = SLIDES[index];

  return (
    <section
      className="relative w-full h-[480px] md:h-[620px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={`${s.title} 焦点海报`}
            title={`${s.title} - 影视介绍`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/10"></div>

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-4">
            <i className="ri-fire-line"></i>
            本周焦点推荐
          </span>
          <h1
            key={slide.id}
            className="font-heading font-black text-3xl md:text-5xl text-background-50 leading-tight animate-fade-in"
          >
            {slide.title}
          </h1>
          <p className="mt-3 text-xs md:text-sm text-background-200 tracking-wide">{slide.meta}</p>
          <p className="mt-4 text-sm md:text-base text-background-100/90 leading-relaxed line-clamp-3 max-w-xl">
            {slide.desc}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.id}`}
              className="h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/category/movie"
              className="h-11 px-6 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-2 border border-background-50/30 transition-colors cursor-pointer"
            >
              <i className="ri-stack-line text-lg"></i>
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 md:bottom-8 right-4 md:right-8 z-10 flex items-center gap-3">
        <div className="flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="w-9 h-9 rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 flex items-center justify-center border border-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="w-9 h-9 rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 flex items-center justify-center border border-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}